import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { 
  ArrowLeft, 
  Bell,
  Wallet,
  TrendingUp,
  Shield,
  Gift,
  MessageSquare
} from "lucide-react";
import { toast } from "sonner";

const NotificationSettings = ({ user }) => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    push_enabled: true,
    email_enabled: true,
    transactions: true,
    security: true,
    promotions: false,
    price_alerts: true,
    weekly_summary: true
  });

  const handleToggle = (key) => {
    setSettings(prev => {
      const newSettings = { ...prev, [key]: !prev[key] };
      toast.success("Preference updated");
      return newSettings;
    });
  };

  const notificationTypes = [
    {
      key: "transactions",
      icon: Wallet,
      label: "Transactions",
      subtitle: "Payments, transfers, and deposits",
      color: "bg-emerald-500/10 text-emerald-400"
    },
    {
      key: "security",
      icon: Shield,
      label: "Security Alerts",
      subtitle: "Login attempts and suspicious activity",
      color: "bg-rose-500/10 text-rose-400"
    },
    {
      key: "price_alerts",
      icon: TrendingUp,
      label: "Price Alerts",
      subtitle: "FX rate changes and investment updates",
      color: "bg-blue-500/10 text-blue-400"
    },
    {
      key: "promotions",
      icon: Gift,
      label: "Promotions",
      subtitle: "Offers and new features",
      color: "bg-purple-500/10 text-purple-400"
    },
    {
      key: "weekly_summary",
      icon: MessageSquare,
      label: "Weekly Summary",
      subtitle: "Your financial activity recap",
      color: "bg-primary/10 text-primary"
    }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="notification-settings-page">
      {/* Header */}
      <header className="px-5 pt-4 pb-2 flex items-center gap-4 max-w-lg mx-auto">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/settings")}
          className="text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-semibold text-xl">Notifications</h1>
      </header>

      <main className="px-5 pb-24 max-w-lg mx-auto space-y-6">
        {/* Master Toggles */}
        <div className="glass rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-white/5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Bell className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium">Push Notifications</p>
                <p className="text-xs text-muted-foreground">Receive alerts on your device</p>
              </div>
            </div>
            <Switch
              checked={settings.push_enabled}
              onCheckedChange={() => handleToggle("push_enabled")}
            />
          </div>
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium">Email Notifications</p>
                <p className="text-xs text-muted-foreground">Get updates in your inbox</p>
              </div>
            </div>
            <Switch
              checked={settings.email_enabled}
              onCheckedChange={() => handleToggle("email_enabled")}
            />
          </div>
        </div>

        {/* Notification Types */}
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground/70 mb-3 px-1">
            Alert Types
          </p>
          <div className="glass rounded-2xl overflow-hidden">
            {notificationTypes.map((type, index) => {
              const Icon = type.icon;
              return (
                <div
                  key={type.key}
                  className={`flex items-center justify-between p-4 ${
                    index < notificationTypes.length - 1 ? "border-b border-white/5" : ""
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${type.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{type.label}</p>
                      <p className="text-xs text-muted-foreground">{type.subtitle}</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings[type.key]}
                    onCheckedChange={() => handleToggle(type.key)}
                    disabled={!settings.push_enabled && !settings.email_enabled}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Info Note */}
        <div className="glass rounded-2xl p-4">
          <p className="text-xs text-muted-foreground">
            Security alerts cannot be fully disabled for your protection. You&apos;ll always receive critical security notifications.
          </p>
        </div>
      </main>
    </div>
  );
};

export default NotificationSettings;
